import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// What each item cost on its latest delivery in the period against the delivery before it, however
// long ago that was, so a supplier's quiet rate rise is caught on the first bill it appears on.
//
// A delivery's cost per unit is everything paid for the item on that bill over every unit of it
// received (free units lower it), the same figure the Medicine Profile shows. An item's first
// delivery is compared with its opening stock, the old system's cost when this one started, since
// for a while that is the only earlier price there is. Free deliveries and cancelled bills are
// never compared. The server works all of that out; this screen only filters by how far a price
// moved and in which direction.

const pct = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n}%`;
};

const signedMoney = (v) => {
  const n = Number(v || 0);
  if (n === 0) return fmtMoney(0);
  return `${n > 0 ? '+' : '-'} ${fmtMoney(Math.abs(n))}`;
};

const changeTone = (v) => {
  const n = Number(v || 0);
  if (n > 0) return 'text-rose-600';
  if (n < 0) return 'text-emerald-600';
  return 'text-gray-500';
};

const THRESHOLDS = [
  { key: 0, label: 'Any change' },
  { key: 5, label: '5% or more' },
  { key: 10, label: '10% or more' },
  { key: 25, label: '25% or more' },
];

const DIRECTIONS = [
  { key: 'all', label: 'Up and down' },
  { key: 'up', label: 'Went up' },
  { key: 'down', label: 'Went down' },
];

// Seven columns, so the table fits a laptop screen beside the sidebar: the biggest move and the
// number of deliveries ride under the change and the date rather than taking columns of their own.
const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

const COLUMNS = [
  {
    key: 'name', label: 'Medicine', wrap: true,
    render: (r) => (
      <div>
        <p className="font-semibold text-gray-900">{r.name}</p>
        {r.genericName && !sameText(r.genericName, r.name) && <p className="text-xs text-gray-500">{r.genericName}</p>}
      </div>
    ),
    csv: (r) => r.name,
    searchText: (r) => `${r.name} ${r.genericName || ''} ${r.latestVendor || ''} ${r.previousVendor || ''}`,
  },
  {
    key: 'latestDate', label: 'Last delivery', wrap: true,
    render: (r) => (
      <div>
        <p className="whitespace-nowrap">{r.latestDate}</p>
        <p className="text-xs text-gray-500">{r.latestVendor || 'No supplier'}</p>
        {Number(r.deliveriesInPeriod) > 1 && (
          <p className="text-[11px] text-gray-400">{r.deliveriesInPeriod} deliveries in the period</p>
        )}
      </div>
    ),
    csv: (r) => `${r.latestDate} ${r.latestVendor || ''}`.trim(),
  },
  {
    key: 'previousCost', label: 'Before', align: 'right',
    render: (r) => (
      <div>
        <p>{fmtMoney(r.previousCost)}</p>
        <p className="text-xs text-gray-500">{r.previousIsOpening ? 'Opening stock' : r.previousDate}</p>
      </div>
    ),
    csv: (r) => `${r.previousCost}${r.previousIsOpening ? ' (opening stock)' : ''}`,
    sortValue: (r) => Number(r.previousCost),
  },
  {
    key: 'latestCost', label: 'Now', align: 'right',
    render: (r) => <span className="font-semibold text-gray-900">{fmtMoney(r.latestCost)}</span>,
    csv: (r) => r.latestCost,
    sortValue: (r) => Number(r.latestCost),
  },
  {
    key: 'changePercent', label: 'Change', align: 'right',
    render: (r) => {
      const change = Number(r.changePercent || 0);
      const move = Number(r.biggestMovePercent || 0);
      return (
        <div>
          <p className={`font-semibold ${changeTone(change)}`}>{change === 0 ? 'No change' : pct(change)}</p>
          {Math.abs(move) > Math.abs(change) && (
            <p className="text-[11px] text-gray-500" title={`Lowest ${fmtMoney(r.lowCost)}, highest ${fmtMoney(r.highCost)}`}>
              Moved {pct(move)} in the period
            </p>
          )}
          {r.supplierChanged && (
            <p className="text-[11px] text-amber-700" title={`Before: ${r.previousVendor || 'no supplier'}`}>Different supplier</p>
          )}
        </div>
      );
    },
    csv: (r) => r.changePercent,
    sortValue: (r) => Number(r.changePercent),
  },
  {
    key: 'sellingNow', label: 'Selling now', align: 'right',
    render: (r) => {
      if (r.sellingNow === null || r.sellingNow === undefined) return <span className="text-gray-300">—</span>;
      const notRaised = Number(r.changePercent) > 0 && r.sellingBefore !== null && r.sellingBefore !== undefined
        && Number(r.sellingBefore) === Number(r.sellingNow);
      const markup = r.markupNow === null || r.markupNow === undefined ? null : Number(r.markupNow);
      return (
        <div>
          <p>{fmtMoney(r.sellingNow)}</p>
          {markup !== null && (
            <p className={`text-xs ${markup < 0 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
              {markup < 0 ? 'Below cost' : `Markup ${pct(markup)}`}
            </p>
          )}
          {notRaised && <p className="text-[11px] text-amber-700">Not raised with the cost</p>}
        </div>
      );
    },
    csv: (r) => r.sellingNow ?? '',
    sortValue: (r) => Number(r.markupNow ?? 0),
  },
  {
    key: 'extraCost', label: 'Cost of the change', align: 'right',
    render: (r) => <span className={`font-semibold ${changeTone(r.extraCost)}`}>{signedMoney(r.extraCost)}</span>,
    csv: (r) => r.extraCost,
    sortValue: (r) => Number(r.extraCost),
  },
];

const PriceChangesReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('last30'));
  const [preset, setPreset] = useState('last30');
  const [threshold, setThreshold] = useState(0);
  const [direction, setDirection] = useState('all');

  const fetcher = useCallback(
    () => reportsAPI.getPriceChanges({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);
  const k = data?.kpis || {};

  // A price that went up and came back down has no change on its last delivery but did move, so
  // the threshold is read against the biggest single move between deliveries in the period.
  const rows = useMemo(
    () => (data?.rows || []).filter((r) => {
      const move = Math.abs(Number(r.biggestMovePercent || 0));
      const change = Number(r.changePercent || 0);
      if (move === 0 || move < threshold) return false;
      if (direction === 'up') return change > 0;
      if (direction === 'down') return change < 0;
      return true;
    }),
    [data, threshold, direction]
  );

  const chartRows = useMemo(
    () => rows
      .filter((r) => Number(r.changePercent) !== 0)
      .sort((a, b) => Math.abs(Number(b.changePercent)) - Math.abs(Number(a.changePercent)))
      .slice(0, 12)
      .map((r) => ({ name: r.name, change: Number(r.changePercent) })),
    [rows]
  );

  const firsts = Number(k.firstPurchases || 0);
  const extra = Number(k.extraCost || 0);
  const emptyText = loading
    ? 'Loading'
    : (data?.rows || []).length === 0
      ? firsts > 0
        ? `Nothing to compare yet: the ${firsts} item${firsts === 1 ? '' : 's'} delivered in this period ${firsts === 1 ? 'was' : 'were'} bought for the first time. A change shows once an item is delivered again.`
        : 'No deliveries in the selected range'
      : 'No price moved this much in the selected range';

  const kpis = [
    { label: 'Items compared', value: k.compared ?? 0, sub: 'Delivered before as well' },
    { label: 'Went up', value: k.rose ?? 0, tone: Number(k.rose) > 0 ? 'alert' : undefined },
    { label: 'Went down', value: k.fell ?? 0 },
    {
      label: 'Biggest rise',
      value: k.biggestRise ? pct(k.biggestRise.percent) : '—',
      sub: k.biggestRise?.name,
      tone: k.biggestRise ? 'alert' : undefined,
    },
    {
      label: 'Cost of changes',
      value: signedMoney(extra),
      sub: 'On this period\'s deliveries',
      tone: extra > 0 ? 'alert' : undefined,
    },
    { label: 'First purchases', value: firsts, sub: 'No earlier price' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <div className="flex gap-x-6 gap-y-2 flex-wrap items-center">
        <div className="flex gap-2 flex-wrap" role="group" aria-label="How far the price moved">
          {THRESHOLDS.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={threshold === t.key}
              onClick={() => setThreshold(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${threshold === t.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap" role="group" aria-label="Direction">
          {DIRECTIONS.map((d) => (
            <button
              key={d.key}
              type="button"
              aria-pressed={direction === d.key}
              onClick={() => setDirection(d.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${direction === d.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Every item delivered in the period whose cost per unit moved: its latest delivery against the
        one before it, however long ago that was. The cost per unit is everything paid for the item on
        the bill over every unit received, so free units lower it. An item's first delivery is compared
        with its opening stock, the old system's cost when this one started. Free deliveries and
        cancelled bills are never compared. Click a row for every delivery of the item.
      </p>

      {chartRows.length > 0 && (
        <ChartPanel title="Biggest changes on the latest delivery">
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 34)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="name" {...axisProps} width={170} />
              <ReferenceLine x={0} stroke="#9ca3af" />
              <Tooltip {...tooltipStyle} formatter={(v) => [pct(v), 'Change']} />
              {/* Colour is the direction here (rose up, emerald down), so not one colour per bar. */}
              <Bar dataKey="change" name="Change" barSize={16}>
                {chartRows.map((r, i) => <Cell key={i} fill={r.change > 0 ? '#e11d48' : '#059669'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'medicineDeliveries', medicineId: r.medicineId })}
        title="Price changes"
        columns={COLUMNS}
        rows={rows}
        csvName={`price-changes-${range.from}-to-${range.to}`}
        emptyText={emptyText}
        noun="item"
        footnote="Before is the delivery just ahead of the latest one, or the opening stock's cost for an item's first delivery. When an earlier delivery in the period moved the price more than the latest did, that move is shown under the change. Cost of the change is what this period's deliveries cost beyond the price of the delivery before each one."
      />

      <ReportPrintLayout
        title="Price Changes"
        subtitle={`Deliveries from ${range.from} to ${range.to}`}
        kpis={kpis.map((x) => ({ label: x.label, value: x.sub && x.label === 'Biggest rise' ? `${x.value} ${x.sub}` : x.value }))}
        tables={[{ title: 'Price changes', columns: COLUMNS, rows }]}
      />

      {drill.node}
    </div>
  );
};

export default PriceChangesReport;
